// src/components/MessageWindow.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessagesSquare, Send, X, GripHorizontal } from "lucide-react";
import { api } from "../scripts/api";

/**
 * Draggable floating message window.
 * Sends to backend route: POST /message/send { text, topicId }
 */
export default function MessageWindow({ open, onClose, user }) {
  const display = useMemo(() => {
    const dn = String(user?.display_name || "").trim();
    const em = String(user?.email || "").trim();
    return dn || em || "";
  }, [user]);

  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [log, setLog] = useState([]);

  // IMPORTANT: keep this consistent with what you want.
  
  const TOPIC_ID = 63;

  const dragRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const STORAGE_KEY = "ims_msg_window_pos";

  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) return saved;
    } catch {}
    return { x: Math.max(20, window.innerWidth - 420), y: 90 };
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {}
  }, [pos]);

  useEffect(() => {
    const onResize = () => {
      const w = 380;
      const h = 360;
      setPos((p) => ({
        x: Math.min(Math.max(10, p.x), Math.max(10, window.innerWidth - w - 10)),
        y: Math.min(Math.max(10, p.y), Math.max(10, window.innerHeight - h - 10)),
      }));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onHeaderMouseDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current.dragging = true;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.originX = pos.x;
    dragRef.current.originY = pos.y;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.dragging) return;

      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;

      const w = 380;
      const h = 360;

      const nextX = dragRef.current.originX + dx;
      const nextY = dragRef.current.originY + dy;

      setPos({
        x: Math.min(Math.max(10, nextX), Math.max(10, window.innerWidth - w - 10)),
        y: Math.min(Math.max(10, nextY), Math.max(10, window.innerHeight - h - 10)),
      });
    };

    const onUp = () => {
      dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [pos.x, pos.y]);

  const sendMessage = async () => {
    const t = String(text || "").trim();
    if (!t) return;

    setBusy(true);
    setStatus("");

    const stamp = new Date().toISOString();
    setLog((prev) => [{ dir: "out", text: t, at: stamp }, ...prev]);

    const payloadText = display ? `${display}: ${t}` : t;

    try {
      const { data } = await api.post("/message/send", {
        topicId: TOPIC_ID,
        text: payloadText,
      });

      if (data?.ok) {
        setStatus(data?.warning ? `Sent ✅ (${data.warning})` : "Sent ✅");
        setText("");
      } else {
        setStatus(data?.error || "Send failed");
      }
    } catch (e) {
      const d = e?.response?.data;
      setStatus(d?.error || d?.message || e?.message || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 380,
        height: 360,
        zIndex: 2000,
        borderRadius: 14,
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        onMouseDown={onHeaderMouseDown}
        style={{
          cursor: "grab",
          userSelect: "none",
          padding: "10px 12px",
          borderBottom: "1px solid var(--card-border)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "rgba(0,0,0,0.02)",
        }}
        title="Drag to move"
      >
        <GripHorizontal size={16} className="muted" />
        <MessagesSquare size={18} />
        <div style={{ fontWeight: 900 }}>Messages</div>
        <div style={{ flex: 1 }} />
        <button className="btn-outline" type="button" onClick={onClose} title="Close">
          <X size={16} />
        </button>
      </div>

      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10, flex: 1, minHeight: 0 }}>
        <div className="muted" style={{ fontSize: 12 }}>
          Sends to Telegram topic <b>#{TOPIC_ID}</b>. Ctrl/⌘ + Enter to send.
        </div>

        {status ? (
          <div className="muted" style={{ fontSize: 12, color: "var(--color-accent)" }}>
            {status}
          </div>
        ) : null}

        <div
          className="card"
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {log.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>
              No messages yet.
            </div>
          ) : (
            log.map((m, idx) => (
              <div key={idx} style={{ fontSize: 12, lineHeight: 1.3 }}>
                <div className="muted" style={{ opacity: 0.85 }}>
                  {m.dir === "out" ? "You" : "Telegram"} • {new Date(m.at).toLocaleString()}
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>{m.text}</div>
              </div>
            ))
          )}
        </div>

        <textarea
          className="input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Type a message…"
          rows={3}
          style={{ resize: "none" }}
        />

        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button
            className="btn"
            type="button"
            onClick={sendMessage}
            disabled={busy || !String(text || "").trim()}
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Send size={16} />
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
